/**
 * Which of the six apply buttons the detail sheet should show.
 *
 * The decision used to live inline in a `<Switch>`, where the order of the
 * `<Match>` children *was* the logic — and one of them sat in the wrong place:
 * the external-URL branch preceded the authentication check, so an anonymous
 * visitor on an `apply_url` offer got the external button. Clicking it fired a
 * mutation that requires a session, the mutation failed, and because the
 * redirect lived inside its `.then()` the button did nothing at all: no login,
 * no redirect, no error (I12/AC 23).
 *
 * Pulled out here because the bug was invisible in review — two adjacent JSX
 * blocks, both plausible — and is now one line in a test.
 */
export type ApplyCtaAction =
  | "closed"
  | "login"
  | "external"
  | "already_applied"
  | "unavailable"
  | "apply";

export type ApplyCtaInput = {
  /** The offer is closed, or its workspace is gone. */
  isClosed: boolean;
  isAuthenticated: boolean;
  /** `apply_url` — the offer is handled on the company's own site. */
  hasExternalUrl: boolean;
  isAlreadyApplied: boolean;
  /** `capabilities.can_apply` — a publisher cannot apply to anything. */
  canApply: boolean;
};

export const resolveApplyCtaAction = (input: ApplyCtaInput): ApplyCtaAction => {
  // A closed offer says so before anything else: no CTA on it is honest.
  if (input.isClosed) return "closed";

  // **Above the external branch, deliberately.** Whatever the offer does with a
  // candidate, an anonymous visitor first needs an account — the external site
  // expects a `ref` we only have for a session, and the counting mutation the
  // external path fires is authenticated anyway.
  if (!input.isAuthenticated) return "login";

  // Still above `already_applied`, as it was: on an external offer the
  // "application" is a click we counted, not something the company confirmed,
  // so we do not tell the user they are done.
  if (input.hasExternalUrl) return "external";

  if (input.isAlreadyApplied) return "already_applied";
  if (!input.canApply) return "unavailable";
  return "apply";
};
